/**
 * Fires a venue's OWN Google Ads purchase conversion (D1: never Punchup's
 * global tag, no dual-send). VenueAdsConversionBridge (an async RSC in
 * `@punchup/venues`) publishes the venue's validated {tagId, purchaseLabel}
 * pairs onto `window.__punchupVenueAdsConversions` for this module to read
 * — mirrors the `__punchupVenueGa4Ids` publish pattern for GA4's
 * cross-domain handoff. This module owns both that window-global key and
 * the `VenueAdsConversionEntry` shape; `@punchup/venues` imports the type
 * from here rather than redefining it.
 *
 * D8: click conversions are dropped entirely — this fires PURCHASES only,
 * called from the Fast Checkout `purchase-complete` handler.
 */

import { SentryFeatureTags, logException, logMessage } from "@punchup/sentry";

export interface VenueAdsConversionEntry {
  tagId: string;
  purchaseLabel: string;
}

// Local type for window with the published venue conversion entries (avoids
// global type pollution, matching the WindowWithGtag pattern below).
type WindowWithVenueAdsConversions = Window &
  typeof globalThis & {
    __punchupVenueAdsConversions?: VenueAdsConversionEntry[];
  };

/**
 * Publish the venue's server-validated conversion entries. Called by
 * PublishVenueAdsConversions (`@punchup/venues`) — the ONLY writer. Keeping
 * the setter here means the `__punchupVenueAdsConversions` key literal
 * exists in exactly one module, alongside its reader.
 */
export const setVenueAdsConversions = (
  entries: VenueAdsConversionEntry[]
): void => {
  (window as WindowWithVenueAdsConversions).__punchupVenueAdsConversions =
    entries;
};

/**
 * Clear the published entries on unmount so a stale/wrong venue's tags
 * can't fire on a later purchase after a client-side navigation.
 */
export const clearVenueAdsConversions = (): void => {
  delete (window as WindowWithVenueAdsConversions).__punchupVenueAdsConversions;
};

type WindowWithGtag = Window &
  typeof globalThis & {
    gtag?: (
      command: string,
      action: string,
      params?: Record<string, unknown>
    ) => void;
  };

export interface TrackVenuePurchaseConversionParams {
  value?: number;
  currency?: string;
  transactionId?: string | null;
}

const MAX_GTAG_RETRIES = 3;
const GTAG_RETRY_DELAY_MS = 500;

const synthesizeTransactionId = (): string =>
  `punchup-${
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  }`;

/**
 * Track a venue's Google Ads purchase conversion. Call this from the
 * checkout purchase-complete handler with the completed order's total and
 * transaction code.
 *
 * An empty/absent `window.__punchupVenueAdsConversions` global means this
 * page simply isn't a configured venue page — the common, legitimate case
 * everywhere outside `/v/[venueSlug]` (and on a venue page before its admin
 * pastes a purchase label, M3) — and is a SILENT no-op: no retry, no Sentry
 * noise. Only once at least one entry exists do we retry for `window.gtag`
 * and report to Sentry after retries exhaust, reusing the legacy
 * `trackGoogleAdsConversion` retry idiom.
 *
 * A missing/empty transaction code still fires, with a synthesized
 * `punchup-<uuid>` transaction_id and a Sentry warning.
 *
 * The whole body fails open: this must never throw into the
 * purchase-complete UI, even if `window.gtag` itself throws.
 *
 * $0 orders record no conversion; an unknown value fires value-less.
 *
 * @param params - value/currency/transactionId for this purchase
 * @param retryCount - internal retry counter (do not set manually)
 */
export const trackVenuePurchaseConversion = (
  params: TrackVenuePurchaseConversionParams,
  retryCount = 0
): void => {
  try {
    if (typeof window === "undefined") return;

    const entries = (window as WindowWithVenueAdsConversions)
      .__punchupVenueAdsConversions;
    if (!entries || entries.length === 0) return; // not a configured venue page — silent no-op

    if (params.value === 0) return;

    const windowWithGtag = window as WindowWithGtag;
    if (typeof windowWithGtag.gtag !== "function") {
      if (retryCount < MAX_GTAG_RETRIES) {
        setTimeout(
          () => trackVenuePurchaseConversion(params, retryCount + 1),
          GTAG_RETRY_DELAY_MS
        );
      } else {
        logException(new Error("gtag not loaded after retries exhausted"), {
          tags: { feature: SentryFeatureTags.GoogleAds },
          extra: { retryCount, params },
        });
      }
      return;
    }

    const hasFiniteValue =
      typeof params.value === "number" &&
      Number.isFinite(params.value) &&
      params.value > 0;

    let transactionId = params.transactionId;
    if (!transactionId) {
      transactionId = synthesizeTransactionId();
      logMessage(
        "trackVenuePurchaseConversion: no transaction code, synthesized one",
        {
          level: "warning",
          tags: { feature: SentryFeatureTags.GoogleAds },
          extra: { entryCount: entries.length, synthesizedId: transactionId },
        }
      );
    }

    for (const entry of entries) {
      windowWithGtag.gtag?.("event", "conversion", {
        send_to: `${entry.tagId}/${entry.purchaseLabel}`,
        transaction_id: transactionId,
        ...(hasFiniteValue
          ? { value: params.value, currency: params.currency ?? "USD" }
          : {}),
      });
    }
  } catch (error) {
    logException(
      error instanceof Error
        ? error
        : new Error("trackVenuePurchaseConversion failed unexpectedly"),
      { tags: { feature: SentryFeatureTags.GoogleAds } }
    );
  }
};
